import { defineStore } from "pinia";
import axios from "axios";
import { client } from "@/main";
import mgr from "@/services/security.js";
import { useCustomerStore } from "@/store/modules/customer";
import { useConfigStore } from "@/store/modules/config";
import { ref } from "vue";
import router from "@/router";
import { useProjectStore } from "@/store/modules/project";
/**
 * Reads a JSON value persisted by the customer/project stores. Those stores also write
 * the literal string "null", hence the extra check.
 */
function readStored(key) {
    const raw = localStorage.getItem(key);
    if (!raw || raw === "null") {
        return null;
    }
    try {
        return JSON.parse(raw);
    }
    catch (e) {
        console.error(`Failed to parse ${key} from localStorage`, e);
        return null;
    }
}
export const useAuthStore = defineStore("auth", () => {
    const user = ref(null);
    const apiUser = ref(null);
    async function getUser() {
        try {
            const tmpUser = await mgr.getUser();
            if (!tmpUser) {
                return;
            }
            if (tmpUser.id_token === user.value?.id_token) {
                setAxiosHeaders(tmpUser);
                return;
            }
            setAxiosHeaders(tmpUser);
            user.value = tmpUser;
        }
        catch (e) {
            console.error(e);
        }
    }
    function setAxiosHeaders(customUser) {
        if (!customUser) {
            if (!user.value)
                return;
            customUser = user.value;
        }
        const authToken = customUser.access_token;
        axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
        axios.defaults.headers.Pragma = "no-cache";
        axios.defaults.headers.common["Cache-Control"] = "no-cache";
        axios.defaults.headers.common["Expires"] = "-1";
        const authMiddleWare = async (url, init, next) => {
            init.headers.set("Authorization", `Bearer ${authToken}`);
            init.headers.set("Pragma", "no-cache");
            init.headers.set("Cache-Control", "no-cache");
            init.headers.set("Expires", "-1");
            return await next(url, init);
        };
        client.use(authMiddleWare);
    }
    async function authenticate(returnPath) {
        await getUser();
        const version = localStorage.getItem("version");
        // dictionary cleaning
        localStorage.removeItem("cz");
        localStorage.removeItem("en");
        /**
         * there is no match in the current app version and the local storage
         * we should clear almost the whole local storage
         */
        if (!version || version != process.env.VUE_APP_VERSION) {
            const configStore = useConfigStore();
            configStore.clearLocalStorage(["selectedCustomer", "selectedProject"]);
        }
        if (user.value) {
            // Authenticated: nothing to redirect for. Drop any stale returnPath left
            // by an earlier authenticate() call that did not consume it.
            localStorage.removeItem("returnPath");
            return;
        }
        const rawReturnPath = localStorage.getItem("returnPath") ?? "";
        // Legacy compat: pre-branch code stored JSON.stringify(returnPath) (e.g. "\"/foo\"").
        let savedReturnPath = rawReturnPath;
        try {
            const parsed = JSON.parse(rawReturnPath);
            if (typeof parsed === "string") {
                savedReturnPath = parsed;
            }
        }
        catch {
            // raw is already the path (new format)
        }
        await signIn({
            returnPath: !["", "/", "null", null].includes(savedReturnPath) ? savedReturnPath : returnPath
        });
    }
    async function signIn(data) {
        user.value = null;
        const configStore = useConfigStore();
        try {
            if (data.returnPath && !["", "/", "null", null].includes(data.returnPath)) {
                localStorage.setItem("returnPath", data.returnPath);
                await mgr.signinRedirect({ state: JSON.stringify({ returnPath: data.returnPath }) });
            }
            else {
                await mgr.signinRedirect();
            }
        }
        catch (e) {
            console.error("[auth] signinRedirect failed:", e);
            configStore.isLoginErrorVisible = true;
            throw e;
        }
    }
    async function reLogin(returnPath) {
        let tmpUser;
        try {
            tmpUser = await mgr.signinSilent();
        }
        catch (e) {
            console.error("[auth] signinSilent failed:", e);
            return false;
        }
        if (!tmpUser?.access_token) {
            return false;
        }
        setAxiosHeaders(tmpUser);
        user.value = tmpUser;
        if (returnPath) {
            router.push(returnPath);
        }
        return true;
    }
    async function signOut() {
        const configStore = useConfigStore();
        configStore.clearLocalStorage(["selectedProject", "selectedCustomer", "currentPath"]);
        const cookies = document.cookie.split(";");
        for (const cookie of cookies) {
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            if (!name.match(/CookieConsent/)) {
                document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
            }
        }
        await mgr.signoutRedirect({ id_token_hint: user.value?.id_token });
        user.value = null;
        apiUser.value = null;
    }
    async function getApiUser() {
        await getUser();
        if (!user.value || !user.value.profile.email) {
            return;
        }
        let highestPrivilege = "";
        let highestCustomerPrivilege = "";
        let highestProjectPrivilege = "";
        let isReseller = false;
        /**
         * A selected customer is required to derive any privilege, a selected project is not:
         * a customer without a single project must still get an apiUser, otherwise every
         * apiUser-gated part of the shell stays hidden — including the notice telling the user
         * there is no project and the button to create one.
         *
         * The stores are the source of truth; localStorage is only a fallback for callers that
         * run before the stores are hydrated.
         */
        const customerStore = useCustomerStore();
        const projectStore = useProjectStore();
        const selectedCustomer = customerStore.selectedCustomer ?? readStored("selectedCustomer");
        const selectedProject = projectStore.selectedProject ?? readStored("selectedProject");
        if (!selectedCustomer?.customerId) {
            return;
        }
        const { data } = await client.path("/users/{id}").method("get").create()({ id: user.value.profile.email });
        // FIXME the endpoint above should return only one user, but it returns an array with one element
        // therefore we need to use any
        const userData = data.data;
        const apiUserTmp = {
            ...userData[0],
            isIotAdmin: false,
            isProjectAdmin: false,
            isCustomerAdmin: false,
            isReseller: false,
            highestPrivilege: "",
            highestCustomerPrivilege: "",
            highestProjectPrivilege: ""
        };
        // check whether user is reseller
        if (customerStore.customerList.some((item) => item.customerId == selectedCustomer.customerId && item.isReseller == true)) {
            isReseller = true;
        }
        /**
         * we have to find the highest user privilege through all projects
         */
        if (apiUserTmp.privileges?.some((privilege) => privilege.privilege === "IoTAdmin")) {
            highestPrivilege = "IotAdmin";
            apiUserTmp.isIotAdmin = true;
        }
        else if (apiUserTmp.privileges?.some((privilege) => privilege.privilege === "CustomerAdmin")) {
            highestPrivilege = "CustomerAdmin";
        }
        else if (apiUserTmp.privileges?.some((privilege) => privilege.privilege === "ProjectAdmin")) {
            highestPrivilege = "ProjectAdmin";
        }
        else {
            highestPrivilege = "";
        }
        /**
         * user is not IotAdmin, must find the highest role for customer and project
         */
        if (highestPrivilege !== "IotAdmin") {
            /**
             * try to find the highest role for selected customer
             */
            if (apiUserTmp.privileges?.some((privilege) => privilege.privilege === "CustomerAdmin" && privilege.customerId === selectedCustomer.customerId)) {
                highestCustomerPrivilege = "CustomerAdmin";
                apiUserTmp.isCustomerAdmin = true;
            }
            else if (apiUserTmp.privileges?.some((privilege) => privilege.privilege === "ProjectAdmin" && privilege.customerId === selectedCustomer.customerId)) {
                highestCustomerPrivilege = "ProjectAdmin";
                apiUserTmp.isProjectAdmin = true;
            }
            /**
             * try to find the highest role for selected project — without a selected project
             * there is nothing project-scoped to derive, so the customer level carries over
             */
            if (selectedProject &&
                apiUserTmp.privileges?.some((privilege) => privilege.privilege === "CustomerAdmin" &&
                    privilege.projectId === selectedProject.projectId &&
                    privilege.customerId === selectedCustomer.customerId)) {
                highestProjectPrivilege = "CustomerAdmin";
                apiUserTmp.isCustomerAdmin = true;
            }
            else if (selectedProject &&
                apiUserTmp.privileges?.some((privilege) => privilege.privilege === "ProjectAdmin" &&
                    privilege.projectId === selectedProject.projectId &&
                    privilege.customerId === selectedCustomer.customerId)) {
                highestProjectPrivilege = "ProjectAdmin";
            }
            else {
                highestProjectPrivilege = highestCustomerPrivilege;
            }
        }
        else {
            apiUserTmp.isProjectAdmin = true;
            apiUserTmp.isCustomerAdmin = true;
        }
        apiUserTmp.isReseller = isReseller;
        apiUserTmp.highestPrivilege = highestPrivilege;
        apiUserTmp.highestCustomerPrivilege = highestCustomerPrivilege;
        apiUserTmp.highestProjectPrivilege = highestProjectPrivilege;
        apiUser.value = apiUserTmp;
    }
    function redirectAfterLogin() {
        const projectStore = useProjectStore();
        const customerStore = useCustomerStore();
        if (!projectStore.selectedProject || !customerStore.selectedCustomer)
            return;
        if (router.currentRoute.value.name == "home") {
            router.push({
                name: "dashboard",
                params: {
                    customerId: customerStore.selectedCustomer.customerId,
                    projectId: projectStore.selectedProject.projectId
                }
            });
        }
        else {
            router.push({
                name: router.currentRoute.value.name?.toString().split("Redir")[0],
                params: {
                    customerId: customerStore.selectedCustomer.customerId,
                    projectId: projectStore.selectedProject.projectId
                }
            });
        }
    }
    return {
        user,
        apiUser,
        getUser,
        authenticate,
        signIn,
        signOut,
        getApiUser,
        setAxiosHeaders,
        redirectAfterLogin,
        reLogin
    };
});
